import React, { useState, useRef } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import { useRules } from '../context/RuleContext';
import config from '../config';
import { toast } from 'react-toastify';

export default function UploadFile({ user }) {
  const { loadFromXml, fetchFromDb } = useRules();
  const [status, setStatus] = useState(null);
  const [errors, setErrors] = useState([]);
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [diff, setDiff] = useState(null);
  const [uploadedContent, setUploadedContent] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const processFile = async (file) => {
    if (!file) return;
    if (!file.name.endsWith('.xml')) { setStatus('error'); setErrors(['File must be .xml']); return; }
    if (file.size > 10 * 1024 * 1024) { setStatus('error'); setErrors(['File too large (max 10MB)']); return; }

    setFileName(file.name);
    setLoading(true);
    setStatus(null);
    setErrors([]);
    setDiff(null);

    const reader = new FileReader();
    reader.onload = async (e) => {
      const xmlString = e.target.result;
      setUploadedContent(xmlString);

      if (!xmlString.includes('<RuleSet')) {
        setStatus('error');
        setErrors(['No <RuleSet> element found']);
        setLoading(false);
        return;
      }

      // Get diff from DB
      try {
        const res = await fetch(`${config.API_BASE}/rulefile/diff`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: xmlString }),
        });
        const diffData = await res.json();
        setDiff(diffData);
      } catch { /* backend offline, skip diff */ }

      // Validate and load locally
      const result = loadFromXml(xmlString);
      setLoading(false);
      if (result.success) {
        setStatus('success');
        setErrors([]);
      } else {
        setStatus('error');
        setErrors(result.errors);
      }
    };
    reader.onerror = () => { setLoading(false); setStatus('error'); setErrors(['Failed to read file']); };
    reader.readAsText(file);
  };

  const handleSaveToDb = async () => {
    if (!uploadedContent) return;
    if (user?.role !== 'ADMIN') {
      toast.error('Only ADMIN can save to the master database');
      return;
    }
    try {
      const res = await fetch(`${config.API_BASE}/rulefile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: uploadedContent, username: user?.username, role: user?.role }),
      });
      const data = await res.json();
      if (data.success) { toast.success('Saved to database!'); fetchFromDb(); }
      else toast.error(data.message);
    } catch { toast.error('Backend not available'); }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>Upload XML File</Typography>

      {/* Drop Zone */}
      <Paper
        onDrop={(e) => { e.preventDefault(); setDragOver(false); processFile(e.dataTransfer.files[0]); }}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          p: 5, mb: 3, textAlign: 'center', cursor: 'pointer',
          border: '2px dashed', borderColor: dragOver ? 'primary.main' : 'grey.300',
          bgcolor: dragOver ? 'primary.50' : 'grey.50',
          transition: 'all 0.2s', '&:hover': { borderColor: 'primary.main' },
        }}
      >
        <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h6">Drop XML file here or click to browse</Typography>
        <Typography variant="body2" color="text.secondary">Validates against Google Flights spec before loading</Typography>
        <input type="file" ref={fileInputRef} hidden accept=".xml" onChange={(e) => { processFile(e.target.files[0]); e.target.value = ''; }} />
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Diff Results */}
      {diff && diff.hasDiff && (
        <Card sx={{ mb: 3, borderLeft: '4px solid #2196f3' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CompareArrowsIcon color="primary" />
              <Typography variant="h6">Diff: Uploaded vs Database</Typography>
            </Box>
            {!diff.dbExists ? (
              <Alert severity="info">No file in database yet. This will be the first version.</Alert>
            ) : (
              <>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item><Chip label={`+${diff.added} added`} color="success" size="small" /></Grid>
                  <Grid item><Chip label={`-${diff.removed} removed`} color="error" size="small" /></Grid>
                  <Grid item><Chip label={`~${diff.changed} changed`} color="warning" size="small" /></Grid>
                  <Grid item><Chip label={`Total: ${diff.totalChanges} changes`} size="small" variant="outlined" /></Grid>
                  <Grid item><Chip label={`DB v${diff.dbVersion} by ${diff.dbLastModifiedBy}`} size="small" variant="outlined" /></Grid>
                </Grid>
                <Box sx={{ maxHeight: 300, overflow: 'auto', bgcolor: '#1e1e1e', borderRadius: 1, p: 1.5, fontFamily: 'monospace', fontSize: 12 }}>
                  {diff.changes?.map((c, i) => (
                    <Box key={i} sx={{ mb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#888' }}>Line {c.line}: </Typography>
                      {c.type === 'added' && <Typography variant="caption" sx={{ color: '#4caf50' }}>+ {c.content}</Typography>}
                      {c.type === 'removed' && <Typography variant="caption" sx={{ color: '#f44336' }}>- {c.content}</Typography>}
                      {c.type === 'changed' && (
                        <>
                          <Typography variant="caption" sx={{ color: '#f44336', display: 'block' }}>- {c.from}</Typography>
                          <Typography variant="caption" sx={{ color: '#4caf50', display: 'block' }}>+ {c.to}</Typography>
                        </>
                      )}
                    </Box>
                  ))}
                  {diff.totalChanges > 100 && <Typography variant="caption" sx={{ color: '#888' }}>...and {diff.totalChanges - 100} more</Typography>}
                </Box>
              </>
            )}
            {user?.role === 'ADMIN' && status === 'success' && (
              <Button variant="contained" color="success" sx={{ mt: 2 }} onClick={handleSaveToDb}>
                Approve & Save to Database
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Success */}
      {status === 'success' && (
        <Card sx={{ borderLeft: '4px solid #4caf50', mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon color="success" />
              <Typography variant="h6" color="success.main">Loaded: {fileName}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">File validated and loaded into the workspace.</Typography>
          </CardContent>
        </Card>
      )}

      {/* Error */}
      {status === 'error' && (
        <Card sx={{ borderLeft: '4px solid #f44336' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ErrorIcon color="error" />
              <Typography variant="h6" color="error.main">Validation Failed</Typography>
            </Box>
            <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
              {errors.map((e, i) => <Typography key={i} variant="body2" color="error">• {e}</Typography>)}
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
